import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { APThemeItem, APThemeSetting, APThemeTypes } from '@astropay/web-core-library';

import storageService from 'common/services/storage-service';

import { getWhiteLabel } from 'common/store/global';

import { getAPThemeSetting, setAPTheme } from '../store/ap-theme';
import { useAppSelector } from './hooks';

export const useApTheme = () => {
  const dispatch = useDispatch();
  const darkTheme = APThemeSetting[0].theme;
  const isWhiteLabel = useAppSelector(getWhiteLabel);
  const settedTheme = isWhiteLabel ? APThemeTypes.ASTROPAY_LIGHT_V2 : APThemeTypes.ASTROPAY_DARK_V2;

  // ignoring light theme for now to avoid unexpected behaviour
  const selectedThemeType = useSelector(getAPThemeSetting);

  dispatch(setAPTheme(settedTheme));

  const onThemeChange = (theme: APThemeItem) => {
    //TODO Investigate
    //color-scheme <meta name="color-scheme" content="dark light" />
    try {
      document.body?.setAttribute('class', theme.className || '');
      document.body.classList.add(theme.themeMode);
      storageService.set('AP_UI_THEME_V2', settedTheme);
      const metaThemeColor = document.head.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (metaThemeColor as any).content = theme.color_A;
      }
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * @deprecated Do not use - use darkTheme instead
   */
  const currentTheme = useMemo(() => {
    const foundTheme = APThemeSetting.find((x) => x.themeType === selectedThemeType);
    if (foundTheme) {
      onThemeChange(foundTheme);
      return foundTheme.theme;
    } else {
      return APThemeSetting[0].theme;
    }
  }, [selectedThemeType]);

  /**
   * @deprecated Do not use - theme option removed
   */
  const themeList = useMemo(() => {
    return process.env.NODE_ENV === 'production'
      ? APThemeSetting.filter((theme) => theme.enabledProd)
      : APThemeSetting;
  }, []);

  /**
   * @deprecated Do not use - theme option removed
   */
  const switchTheme = () => {
    const actualTheme = storageService.get('AP_UI_THEME_V2');
    const index = themeList.findIndex((theme) => theme.themeType === actualTheme);
    const nextIndex = index + 1 >= themeList.length ? 0 : index + 1;
    setTheme(themeList[nextIndex]);
  };

  /**
   * @deprecated Do not use - theme option removed
   */
  const setThemeByType = (themeType: APThemeTypes) => {
    const foundTheme = APThemeSetting.find((x) => x.themeType === themeType);
    if (foundTheme) {
      setTheme(foundTheme);
    }
  };

  /**
   * @deprecated Do not use - theme option removed
   */
  const setTheme = (apTheme: APThemeItem) => {
    onThemeChange(apTheme);
    dispatch(setAPTheme(apTheme.themeType));
  };

  /**
   * @deprecated Do not use - theme option removed
   */
  const getCurrentThemeItem = () => {
    const foundTheme = APThemeSetting.find((x) => x.themeType === selectedThemeType);
    if (foundTheme) {
      return foundTheme;
    } else {
      return APThemeSetting[0];
    }
  };

  /**
   * @deprecated Do not use - theme option removed
   */
  const currentThemePaletteType = useMemo(() => {
    const foundTheme = APThemeSetting.find((x) => x.themeType === selectedThemeType);
    if (foundTheme) {
      return foundTheme.theme.palette.type;
    } else {
      return APThemeSetting[0].theme.palette.type;
    }
  }, [selectedThemeType]);

  return {
    darkTheme,
    selectedThemeType,
    currentTheme,
    getCurrentThemeItem,
    currentThemePaletteType,
    themeList,
    setThemeByType,
    setTheme,
    switchTheme,
  };
};
